<template>
  <div class="kb">
    <div v-for="(row, idx) in rows" :key="idx" class="kb-row">
      <button
        v-for="key in row"
        :key="key"
        type="button"
        class="key"
        :class="keyClass(key)"
        :data-key="key"
        :disabled="key === 'ENTER' && !canSubmit"
        :aria-label="ariaLabel(key)"
        @click="$emit('press', key)"
      >
        <AppIcon v-if="key === 'BACK'" name="backspace" size="lg" />
        <template v-else-if="key === 'ENTER'">
          <AppIcon name="enter" size="sm" />ENVIAR
        </template>
        <template v-else>
          {{ key }}
          <!-- O símbolo do estado, junto da cor: a cor sozinha não pode carregar a
               informação, e quem não distingue verde de âmbar leria uma tecla muda
               (diretiva 09). -->
          <i v-if="stateOf(key)" class="mark" aria-hidden="true">{{ SYMBOLS[stateOf(key)] }}</i>
        </template>
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import AppIcon from '../ui/AppIcon.vue';

/* Símbolo por estado. O círculo vazado é o "existe, mas não aqui": ele lê como incompleto
   mesmo sem cor, que é o ponto — o ✓ e o ✕ preenchidos se confundiriam nesse tamanho. */
const SYMBOLS = { right: '✓', moved: '○', absent: '✕' };

const WORDS = {
  right: 'no lugar certo',
  moved: 'no lugar errado',
  absent: 'não está no que falta',
};

export default {
  name: 'VirtualKeyboard',
  components: { AppIcon },
  props: {
    canSubmit: { type: Boolean, default: false },
    hitKey: { type: String, default: null },
    /**
     * O que já se sabe de cada letra tentada, vindo do servidor: `{ A: 'right' }`.
     *
     * Um estado por letra — o melhor que os palpites já deram, e ele não regride. Vazio
     * nos modos que pontuam o ranking, onde o palpite não devolve retorno por casa.
     */
    results: { type: Object, default: () => ({}) },
  },
  emits: ['press'],
  data() {
    return { SYMBOLS };
  },
  computed: {
    ...mapGetters(['preferences']),
    /**
     * As três fileiras.
     *
     * Apagar fica na linha do meio e enviar na última, à direita: é onde os dois ficam no
     * teclado do celular, e é o lado em que o polegar já está. Antes o ENTER abria a
     * última linha, à esquerda, contra o gesto de quem digita.
     *
     * Em ABC as letras trocam de lugar, e só elas: quem escolhe ABC não decorou QWERTY, e
     * mudar também as teclas de ação faria a pessoa reaprender o que não pediu.
     */
    rows() {
      const letters = this.preferences.keyboard === 'abc'
        ? ['ABCDEFGHIJ', 'KLMNOPQRS', 'TUVWXYZ']
        : ['QWERTYUIOP', 'ASDFGHJKL', 'ZXCVBNM'];
      return [
        letters[0].split(''),
        [...letters[1].split(''), 'BACK'],
        [...letters[2].split(''), 'ENTER'],
      ];
    },
  },
  methods: {
    /**
     * O que se sabe desta letra. Vem pronto do servidor, que é quem conhece a palavra —
     * a tela não deduz nem guarda estado próprio, senão recarregar perderia o teclado.
     */
    stateOf(key) {
      const state = this.results[key];
      return SYMBOLS[state] ? state : null;
    },
    keyClass(key) {
      const state = this.stateOf(key);
      return {
        'key-wide': key === 'ENTER' || key === 'BACK',
        'key-enter': key === 'ENTER',
        'key-back': key === 'BACK',
        'is-hit': key === this.hitKey,
        [`res-${state}`]: !!state,
      };
    },
    /**
     * O estado por extenso, para quem usa leitor de tela: símbolo desenhado não é lido, e
     * sem isto a tecla colorida não existiria para essa pessoa.
     */
    ariaLabel(key) {
      if (key === 'BACK') return 'Apagar';
      if (key === 'ENTER') return 'Enviar palpite';
      const state = this.stateOf(key);
      return state ? `${key} — ${WORDS[state]}` : key;
    },
  },
};
</script>

<style scoped>
/* Teclado próprio, e não o do sistema: a partida aceita 26 letras e nada mais, e o
   teclado nativo traria número, emoji e autocorreção para uma caixa que não os aceita. */
.kb {
  flex: none;
  /* Dez teclas numa tela de 360px: elas encolhem, e o teclado não empurra a coluna. */
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 7px;
  /* Respiro lateral mínimo: o que sobra nas bordas some da tecla, e a tecla é o alvo. */
  padding: 8px 4px calc(10px + env(safe-area-inset-bottom));
  background: var(--veil-1);
  border-top: 1px solid var(--line);
}

.kb-row {
  display: flex;
  gap: 5px;
  justify-content: center;
  width: 100%;
  max-width: 620px;
  margin: 0 auto;
  min-width: 0;
}

.key {
  flex: 1 1 0;
  min-width: 0;
  /* Sem isto, dois toques rápidos no apagar disparam o zoom de duplo-toque do Safari —
     e apagar duas letras seguidas é exatamente isso. `manipulation` desliga o
     duplo-toque sem desligar o pinch do resto da página. */
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  height: 46px;
  display: grid;
  place-items: center;
  border-radius: var(--r-field);
  background: var(--surface-2);
  border: 1px solid var(--line);
  color: var(--text);
  font: 700 15px/1 var(--f-body);
  transition: transform 0.07s, filter 0.12s, background 0.12s, border-color 0.12s;
}

.key:hover { background: var(--raised); }

/* A tecla afunda um pixel e clareia: o toque tem resposta sem precisar de sombra falsa. */
.key:active,
.key.is-hit {
  transform: translateY(1px);
  filter: brightness(1.25);
  border-color: var(--line-2);
}

.key-wide { flex: 1.4 1 0; }

/* Diretiva 11: o gradiente é do CTA, e na partida o CTA é enviar. */
.key-enter {
  flex: 2.6 1 0;
  background: var(--brand-gradient);
  border-color: transparent;
  color: var(--on-brand);
  font-family: var(--f-label);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 0 4px;
  overflow: hidden;
}

.key-enter:hover:not(:disabled) { filter: brightness(1.1); }
.key-enter:disabled { opacity: 0.4; cursor: not-allowed; }

.key-back { color: var(--error-light); }

/* Quem tem ponteiro fino tem teclado físico, e o `keydown` da tela já o escuta. O teclado
   virtual ocupava espaço numa tela de desktop sem servir para nada. Ele volta em qualquer
   tela de toque, inclusive tablet em paisagem. */
@media (pointer: fine) and (min-width: 1024px) {
  .kb { display: none; }
}
/* ── teclas pintadas pelo retorno ──────────────────────────────────────────────
   Mesma linguagem do dock da casa. Um estado por tecla — o melhor que os palpites já
   deram —, porque duas notícias na mesma tecla se leem como letra repetida na palavra. */
.key.res-right { background: var(--ok); border-color: var(--ok); color: var(--bg); }
.key.res-moved { background: var(--amber); border-color: var(--amber); color: var(--bg); }
.key.res-absent { background: var(--veil-1); border-color: var(--line); color: var(--text-3); }

/* A letra fica no meio e o símbolo embaixo dela, na cor que a tecla já tem. */
.key { position: relative; }

.mark {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 3px;
  font: 700 9px/1 var(--f-body);
  font-style: normal;
  color: inherit;
  opacity: 0.85;
}

/* A letra sobe um pouco para não encostar no símbolo. */
.key.res-right,
.key.res-moved,
.key.res-absent { padding-bottom: 11px; }

</style>
